import {
  AccountsIcon,
  AssignedAccountIcon,
  DashboardIcon,
  RecruitmentIcon,
  ReportIcon,
  TaskIcon,
} from "components/icons";

import { envChecker } from "helpers/envChecker";

import { ENav, ENavTab, TNavItem } from "./types";
import { APP_ACCESS_RIGHT } from "constants/accessRight";
import { EENV } from "constants/index";
import { routePaths } from "routes/constants";

export const DEFAULT_NAV_ITEMS: TNavItem[] = [
  ...(envChecker({ envs: [EENV.DEV, EENV.QA] })
    ? [
        {
          name: ENav.DASHBOARD,
          label: "dashboard",
          href: routePaths.root.pBuSlug.root,
          icon: <DashboardIcon width={20} height={20} />,
        },
      ]
    : []),
  {
    name: ENav.ASSIGNED_ACCOUNT,
    label: "assigned_account",
    href: routePaths.root.pBuSlug.assignedAccount.root,
    icon: <AssignedAccountIcon width={20} height={20} />,
  },
  {
    name: ENav.MANAGE_TASK,
    label: "manage_tasks",
    href: routePaths.root.pBuSlug.manageTask.root,
    icon: <TaskIcon width={20} height={20} />,
  },
  {
    name: ENav.RECRUITMENT,
    label: "recruitment",
    href: routePaths.root.pBuSlug.recruitment.root,
    icon: <RecruitmentIcon width={20} height={20} />,
  },
  {
    name: ENav.REPORTS,
    label: "reports",
    href: routePaths.root.pBuSlug.eodReport.root,
    icon: <ReportIcon width={20} height={20} />,
  },
  {
    name: ENav.MEMBERS,
    label: "members",
    href: routePaths.root.pBuSlug.members.root,
    icon: <AccountsIcon width={20} height={20} />,
  },
];

export const PATHS_TO_HIDE = [...DEFAULT_NAV_ITEMS];

export const SIDEBAR_WIDTH = 56;

export const MAX_LENGTH_NAV_CHILDREN = 5;

export const MINIMUM_MAIN_PATH_LENGTH = {
  [ENav.DASHBOARD]: 1,
  [ENav.ASSIGNED_ACCOUNT]: 1,
  [ENav.MANAGE_TASK]: 1,
  [ENav.RECRUITMENT]: 1,
  [ENav.REPORTS]: 1,
  [ENav.MEMBERS]: 1,
  [ENav.PENDING_REPORT]: 1,
};

export const TAB_LABEL: Record<ENavTab, string> = {
  [ENavTab.COMPANY_MATCHING_LIST]: "company_matching_list",
  [ENavTab.STAFF_MATCHING_LIST]: "staff_matching_list",
  [ENavTab.KANBAN_VIEW]: "kanban_view",
  [ENavTab.LIST_VIEW]: "list_view",
  [ENavTab.TODAYS_REPORT]: "todays_report",
  [ENavTab.EOD_HISTORY]: "eod_history",
  [ENavTab.PENDING_REPORT]: "pending_report",
};

export const TAB_LIST: Record<string, Record<string, string>> = {
  [ENavTab.COMPANY_MATCHING_LIST]: {
    [ENav.RECRUITMENT]:
      routePaths.root.pBuSlug.recruitment.companyMatchingList.root,
  },
  [ENavTab.STAFF_MATCHING_LIST]: {
    [ENav.RECRUITMENT]:
      routePaths.root.pBuSlug.recruitment.staffMatchingList.root,
  },
  [ENavTab.KANBAN_VIEW]: {
    [ENav.MANAGE_TASK]: routePaths.root.pBuSlug.manageTask.kanbanView.root,
  },
  [ENavTab.LIST_VIEW]: {
    [ENav.MANAGE_TASK]: routePaths.root.pBuSlug.manageTask.listView.root,
  },
  [ENavTab.TODAYS_REPORT]: {
    [ENav.REPORTS]: routePaths.root.pBuSlug.eodReport.todaysReport.root,
  },
  [ENavTab.EOD_HISTORY]: {
    [ENav.REPORTS]: routePaths.root.pBuSlug.eodReport.eodHistory.root,
  },
  [ENavTab.PENDING_REPORT]: {
    [ENav.PENDING_REPORT]: routePaths.root.pBuSlug.pendingReport.root,
  },
};

export const TAB_LIST_ACCESSRIGHT: Record<
  string,
  Record<string, number[] | null>
> = {
  [ENavTab.COMPANY_MATCHING_LIST]: {
    [ENav.RECRUITMENT]: [APP_ACCESS_RIGHT.AI_MATCHING_VIEW],
  },
  [ENavTab.STAFF_MATCHING_LIST]: {
    [ENav.RECRUITMENT]: [APP_ACCESS_RIGHT.AI_MATCHING_VIEW],
  },
  [ENavTab.KANBAN_VIEW]: {
    [ENav.MANAGE_TASK]: [
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_LINKED_ASSIGNED_TICKETS,
    ],
  },
  [ENavTab.LIST_VIEW]: {
    [ENav.MANAGE_TASK]: [
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ALL_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_STAFF_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_CLIENT_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_MATCH_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_ASSIGNED_AD_HOC_TICKETS,
      APP_ACCESS_RIGHT.KANBAN_READ_LINKED_ASSIGNED_TICKETS,
    ],
  },
  [ENavTab.TODAYS_REPORT]: {
    [ENav.REPORTS]: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_ASSIGNED_EODS,
    ],
  },
  [ENavTab.EOD_HISTORY]: {
    [ENav.REPORTS]: [
      APP_ACCESS_RIGHT.EOD_MANAGE_ALL_EOD,
      APP_ACCESS_RIGHT.EOD_MANAGE_PAST_DUE_EOD,
    ],
  },
  [ENavTab.PENDING_REPORT]: { [ENav.PENDING_REPORT]: null },
};

export const ENAV_TYPE = {};

export const MAP_SIDEBAR_NAV: Record<string, ENav> = {
  dashboard: ENav.DASHBOARD,
  "assigned-account": ENav.ASSIGNED_ACCOUNT,
  recruitment: ENav.RECRUITMENT,
  "eod-report": ENav.REPORTS,
  "manage-task": ENav.MANAGE_TASK,
  members: ENav.MEMBERS,
  "pending-report": ENav.PENDING_REPORT,
};
